import range from 'lodash/range'
import type moment from 'moment'

import type Allowance from 'modules/timeoff/store/models/Allowance'

/*
  rangeByDay takes two dates in moment format and returns
  an array of dates in moment format, one for each day between
  start and end, inclusive.
*/
export default function rangeByDay(start: moment.Moment, end: moment.Moment): moment.Moment[] {
  const diff = Math.abs(start.diff(end, 'day')) + 1

  return range(diff).map((i) => start.clone().add(i, 'day'))
}

/*
  rangeByDayWithFrenchCalendar takes two dates in moment format and returns
  an array of dates in moment format, one for each day between
  start and end, inclusive, respecting French calendar rules for leave display.

  For "jours ouvrables" (french_calendar_days), Saturdays should be excluded from
  the visual display to match backend calculation (backend includes them in range
  but doesn't count them in leave duration).
  For "jours ouvrés" (french_ouvres), both Saturdays and Sundays are excluded.
*/
export function rangeByDayWithFrenchCalendar(
  start: moment.Moment,
  end: moment.Moment,
  allowance: Allowance | null | undefined
): moment.Moment[] {
  const allDays = rangeByDay(start, end)

  // If no allowance or not French calendar days, return all days
  if (!allowance || (!allowance.frenchCalendarDays && !allowance.frenchOuvre)) {
    return allDays
  }

  // For "jours ouvrés" (french_ouvres), exclude both Saturdays and Sundays
  if (allowance.frenchOuvre) {
    return allDays.filter((day) => day.day() !== 0 && day.day() !== 6) // 0 = Sunday, 6 = Saturday
  }

  // For "jours ouvrables" (french_calendar_days), exclude Sundays from visual display
  // to match backend calculation (backend excludes Sundays from counting)
  if (allowance.frenchCalendarDays) {
    return allDays.filter((day) => day.day() !== 0) // 0 = Sunday
  }

  return allDays
}
